'use client';

import { useTranslation } from '../hooks/useTranslation';

export type ToastType = 'alert' | 'warning' | 'success' | 'info';

export type ToastItemData = {
    id: string;
    type: ToastType;
    message: string;
    title?: string;
    duration: number;
};

const typeStyles: Record<
    ToastType,
    { wrap: string; icon: string; bar: string; glow: string }
> = {
    success: {
        wrap: 'border-emerald-400 text-emerald-200',
        icon: 'text-emerald-400',
        bar: 'bg-emerald-400',
        glow: 'shadow-[0_0_20px_rgba(63,185,80,0.18)]',
    },
    alert: {
        wrap: 'border-red-400 text-red-200',
        icon: 'text-red-400',
        bar: 'bg-red-400',
        glow: 'shadow-[0_0_20px_rgba(248,113,113,0.16)]',
    },
    warning: {
        wrap: 'border-amber-400 text-amber-200',
        icon: 'text-amber-400',
        bar: 'bg-amber-400',
        glow: 'shadow-[0_0_20px_rgba(251,191,36,0.16)]',
    },
    info: {
        wrap: 'border-sky-400 text-sky-200',
        icon: 'text-sky-400',
        bar: 'bg-sky-400',
        glow: 'shadow-[0_0_20px_rgba(56,189,248,0.16)]',
    },
};

function ToastIcon({ type }: { type: ToastType }) {
    const cls = `h-5 w-5 flex-shrink-0 ${typeStyles[type].icon}`;
    if (type === 'success') {
        return (
            <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
        );
    }
    if (type === 'alert') {
        return (
            <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
        );
    }
    if (type === 'warning') {
        return (
            <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
            </svg>
        );
    }
    return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
        </svg>
    );
}

export default function Toast({
    toast,
    leaving,
    paused,
    onClose,
    onPause,
    onResume,
}: {
    toast: ToastItemData;
    leaving: boolean;
    paused: boolean;
    onClose: () => void;
    onPause: () => void;
    onResume: () => void;
}) {
    const { t } = useTranslation();
    const style = typeStyles[toast.type] ?? typeStyles.info;
    const role = toast.type === 'alert' ? 'alert' : 'status';

    return (
        <div
            role={role}
            aria-live={toast.type === 'alert' ? 'assertive' : 'polite'}
            onMouseEnter={onPause}
            onMouseLeave={onResume}
            className={`relative w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-lg border-2 bg-[#0d1117] ${style.wrap} ${style.glow} ${
                leaving ? 'app-toast-leave' : 'app-toast-enter'
            }`}
        >
            <div className="flex items-start gap-3 px-4 py-3 pr-2">
                <div className="pt-0.5">
                    <ToastIcon type={toast.type} />
                </div>
                <div className="min-w-0 flex-1">
                    {toast.title ? (
                        <p className="text-sm font-semibold leading-snug">{toast.title}</p>
                    ) : null}
                    <p className={`text-sm font-medium leading-snug ${toast.title ? 'mt-0.5 opacity-90' : ''}`}>
                        {toast.message}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-shrink-0 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/25"
                    aria-label={t('common.close')}
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="h-[3px] w-full bg-black/40" aria-hidden>
                <div
                    className={`app-toast-progress h-full ${style.bar}`}
                    style={{
                        animationDuration: `${toast.duration}ms`,
                        animationPlayState: paused || leaving ? 'paused' : 'running',
                    }}
                />
            </div>
        </div>
    );
}
